import {
  CaretLeftIcon,
  CodeIcon,
  GlobeIcon,
  PlayIcon,
  SparkleIcon,
  SpinnerIcon,
  StopIcon,
} from '@phosphor-icons/react'
import { CodeEditor, CodeFile } from 'mini-code'
import { useEffect, useRef, useState } from 'react'
import { dspscript } from './dsp-script.ts'
import { functionDefinitions } from './function-definitions.ts'
import { NavBar } from './NavBar.tsx'
import { Link, navigate } from './Router.tsx'
import { ScrollingVisualizer } from './ScrollingVisualizer.tsx'
import {
  type GeneratedTrack,
  useAIGeneratedTracksStore,
  useAISettingsStore,
  useEditorSettingsStore,
  useLatencyHintStore,
  useNavBarHeightStore,
  useSessionStore,
} from './store.ts'
import { useTheme } from './theme.ts'
import { tokenizer } from './tokenizer.ts'

export function AIGeneratePage() {
  const theme = useTheme()
  const hideFunctionSignatures = useEditorSettingsStore(state => state.hideFunctionSignatures)
  const wordWrap = useEditorSettingsStore(state => state.wordWrap)
  const [message, setMessage] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState('')
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null)
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false)
  const { temperature, topP, model, setTemperature, setTopP, setModel } = useAISettingsStore()
  const { latencyHint } = useLatencyHintStore()
  const { height: navBarHeight } = useNavBarHeightStore()
  const {
    tracks: generatedTracks,
    lastPlayedTrackId,
    addTrack,
    setLastPlayedTrackId,
  } = useAIGeneratedTracksStore()
  const codeFilesRef = useRef<Map<string, CodeFile>>(new Map())

  useEffect(() => {
    const checkScreenSize = () => {
      setIsVerySmallScreen(window.innerWidth < 700)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      const currentlyPlaying = dspscript.isPlaying()
      if (!currentlyPlaying && playingTrackId) {
        setPlayingTrackId(null)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [playingTrackId])

  const getCodeFile = (trackId: string, code: string): CodeFile => {
    if (!codeFilesRef.current.has(trackId)) {
      codeFilesRef.current.set(trackId, new CodeFile(code))
    }
    else {
      const codeFile = codeFilesRef.current.get(trackId)!
      if (codeFile.value !== code) {
        codeFile.value = code
      }
    }
    return codeFilesRef.current.get(trackId)!
  }

  const handleGenerate = async () => {
    if (!message.trim() || isGenerating) return

    setIsGenerating(true)
    setError('')

    try {
      const response = await fetch('/api/ai/generate-track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: message.trim(),
          temperature,
          topP,
          model,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate loop' }))
        setError(errorData.error || 'Failed to generate loop')
        return
      }

      const { title, code } = await response.json()
      if (!code) {
        setError('No code received from AI')
        return
      }

      const newTrack: GeneratedTrack = {
        id: `track-${Date.now()}`,
        title: title || 'Untitled',
        code,
      }

      addTrack(newTrack)
    }
    catch (err: any) {
      setError(err.message || 'Failed to generate loop')
    }
    finally {
      setIsGenerating(false)
    }
  }

  const handlePlay = async (track: GeneratedTrack) => {
    try {
      setError('')
      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      await dspscript.loadProgram(track.code)
      await dspscript.start()
      setPlayingTrackId(track.id)
      setLastPlayedTrackId(track.id)
    }
    catch (err: any) {
      setError(err.message)
      setPlayingTrackId(null)
    }
  }

  const handleStop = async () => {
    try {
      await dspscript.stop()
      setPlayingTrackId(null)
    }
    catch (err: any) {
      setError(err.message)
    }
  }

  const handleLoadInEditor = (track: GeneratedTrack) => {
    const wasPlaying = playingTrackId === track.id && dspscript.isPlaying()
    useSessionStore.getState().setRemixTrack({
      code: track.code,
      title: track.title,
      artist: 'GPT',
      wasPlaying,
    })
    useSessionStore.getState().setSkipExampleOnce(true)
    navigate('/editor')
  }

  const handleNavBarPlay = async () => {
    if (dspscript.isPlaying()) {
      return
    }
    if (generatedTracks.length > 0) {
      let trackToPlay: GeneratedTrack | null = null

      if (lastPlayedTrackId) {
        trackToPlay = generatedTracks.find(t => t.id === lastPlayedTrackId) || null
      }

      if (!trackToPlay) {
        trackToPlay = generatedTracks[0]
      }

      await handlePlay(trackToPlay)
    }
  }

  const handleNavBarStop = async () => {
    try {
      await dspscript.stop()
      setPlayingTrackId(null)
    }
    catch (err: any) {
      setError(err.message)
    }
  }

  const rightSideButtons = (
    <>
      <Link
        href="/"
        className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-lime-400 to-green-600 text-white hover:opacity-90 flex items-center gap-2"
      >
        <GlobeIcon weight="regular" size={16} />
        <span className="hidden md:inline">Browse All</span>
      </Link>
      {!isVerySmallScreen && (
        <Link
          href="/editor"
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-500 to-red-600 text-white hover:opacity-90 flex items-center gap-2"
        >
          <CodeIcon weight="regular" size={16} />
          Switch to Editor
        </Link>
      )}
    </>
  )

  return (
    <div className="w-[100dvw] h-[100dvh] overflow-hidden text-[#f0f0f0] flex flex-col">
      <NavBar
        onPlay={handleNavBarPlay}
        onStop={handleNavBarStop}
        rightSideButtons={rightSideButtons}
      />

      <div className="relative" style={{ height: `calc(100dvh - ${navBarHeight}px)` }}>
        <div className="relative h-full overflow-y-auto overflow-x-hidden p-10">
          <div className="max-w-4xl mx-auto flex flex-col gap-6">
            {/* Header */}
            <h2 className="text-4xl font-extrabold font-[Turret_Road] text-pink-500 flex items-center justify-center gap-3">
              <SparkleIcon weight="regular" size={40} />
              Generate a track
            </h2>

            {/* AI Settings */}
            <div className="bg-gradient-to-b from-neutral-800 to-black border border-[#444] rounded-lg p-6">
              <div className="space-y-4 mb-6">
                {/* Model selector */}
                <div className="flex items-center gap-4">
                  <label className="text-white text-sm font-medium">Model</label>
                  <select
                    value={model}
                    onChange={e => setModel(e.target.value as 'gpt-5-chat-latest' | 'chatgpt-4o-latest')}
                    disabled={isGenerating}
                    className="px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm focus:outline-none focus:border-pink-500"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="gpt-5-chat-latest" className="bg-neutral-900 text-white">
                      gpt-5-chat-latest
                    </option>
                    <option value="chatgpt-4o-latest" className="bg-neutral-900 text-white">
                      chatgpt-4o-latest
                    </option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Temperature control */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-white text-sm font-medium">Temperature</label>
                      <span className="text-[#888] text-sm">{temperature.toFixed(1)}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={temperature}
                      onChange={e => setTemperature(parseFloat(e.target.value))}
                      disabled={isGenerating}
                      className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
                    />
                    <div className="flex justify-between text-xs text-[#666]">
                      <span>Focused</span>
                      <span>Creative</span>
                    </div>
                  </div>

                  {/* Top P control */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-white text-sm font-medium">Top P</label>
                      <span className="text-[#888] text-sm">{topP.toFixed(2)}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={topP}
                      onChange={e => setTopP(parseFloat(e.target.value))}
                      disabled={isGenerating}
                      className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
                    />
                    <div className="flex justify-between text-xs text-[#666]">
                      <span>Narrow</span>
                      <span>Broad</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prompt input */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-white font-semibold">What would you like to create?</div>
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !message.trim()}
                    className="px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 shrink-0 flex items-center gap-2"
                  >
                    {isGenerating
                      ? (
                        <>
                          <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                          <span>Generating...</span>
                        </>
                      )
                      : (
                        <>
                          <SparkleIcon weight="regular" size={16} />
                          <span>Generate</span>
                        </>
                      )}
                  </button>
                </div>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={e => {
                    if (
                      e.key === 'Enter'
                      && (e.metaKey || e.ctrlKey)
                      && !isGenerating
                      && message.trim()
                    ) {
                      e.preventDefault()
                      handleGenerate()
                    }
                  }}
                  disabled={isGenerating}
                  placeholder="e.g., A hypnotic techno loop with a deep bass line and ethereal pads..."
                  className="w-full h-32 px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm resize-none focus:outline-none focus:border-pink-500"
                />
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-500 text-white px-4 py-3 text-sm border border-red-600 rounded">
                {error}
              </div>
            )}

            {/* Generated tracks */}
            {generatedTracks.map(track => (
              <div
                key={track.id}
                className="border-b border-b-[#333] bg-black rounded-lg overflow-hidden"
              >
                <div className="p-3 border border-[#333] border-b-0 relative">
                  <ScrollingVisualizer isPlaying={playingTrackId === track.id} />
                  <div className="flex flex-row flex-wrap items-center justify-between gap-2 relative z-10">
                    <div className="flex gap-3 flex-1 flex-wrap">
                      <div className="flex gap-2">
                        {playingTrackId !== track.id
                          ? (
                            <button
                              onClick={() => handlePlay(track)}
                              className="px-3 h-8 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 flex items-center"
                              title="Play"
                            >
                              <PlayIcon weight="fill" size={16} />
                            </button>
                          )
                          : (
                            <button
                              onClick={handleStop}
                              className="px-3 h-8 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:bg-neutral-800 flex items-center"
                              title="Stop"
                            >
                              <StopIcon weight="fill" size={16} />
                            </button>
                          )}
                        <button
                          onClick={() => handleLoadInEditor(track)}
                          className="hidden md:flex px-3 h-8 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 items-center"
                          title="Load loop in editor"
                        >
                          <CaretLeftIcon weight="regular" size={16} />
                        </button>
                      </div>
                      <div
                        className="flex font-semibold text-normal items-center gap-x-1 min-w-0 w-full md:w-auto flex-wrap"
                        style={{ textShadow: '1px 1px 0 #000' }}
                      >
                        <span className="text-[#888]">GPT</span>
                        <span className="text-[#888]">-</span>
                        <span className="whitespace-normal md:whitespace-nowrap min-w-0 break-words">
                          {track.title}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="h-[400px] overflow-hidden">
                  <CodeEditor
                    wordWrap={wordWrap}
                    gutter
                    codeFile={getCodeFile(track.id, track.code)}
                    theme={theme}
                    tokenizer={tokenizer}
                    functionDefinitions={functionDefinitions}
                    errors={[]}
                    autoHeight={false}
                    hideFunctionSignatures={hideFunctionSignatures}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
